import {
  useDataProvider,
  useGetList,
  useLocaleState,
  useNotify,
  usePermissions,
  useTranslate,
} from "ra-core";
import { useId, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ObjectionEditor, SELECT_CLASS } from "./ObjectionEditor";
import {
  adminObjections,
  emptyObjectionForm,
  toObjectionForm,
  tradeLabel,
  tradeOptions,
  type Objection,
  type ObjectionData,
} from "./objectionModel";

type Editing = { mode: "create" } | { mode: "edit"; objection: Objection };

/**
 * GAVION X2: /parametres/objections. Admins create, edit, deactivate and reactivate; reps (and anyone whose
 * role is not yet known) get a read-only list of the active objections. RLS enforces the same split.
 */
export const ObjectionsSettingsPage = () => {
  const t = useTranslate();
  const notify = useNotify();
  const dp = useDataProvider();
  const [locale = "fr"] = useLocaleState();
  const { permissions } = usePermissions();
  const admin = permissions === "admin";
  const filterId = useId();
  const { data = [], refetch } = useGetList<Objection>("objections", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "sort_order", order: "ASC" },
  });
  const [trade, setTrade] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const rows = useMemo(
    () => adminObjections(data, trade, admin && showInactive),
    [data, trade, admin, showInactive],
  );
  // Trades present in the data but unknown to the trade map stay filterable.
  const tradeChoices = useMemo(() => {
    const known = tradeOptions(locale);
    const extra = [...new Set(data.map((o) => o.trade))]
      .filter((v): v is string => !!v && !known.some((k) => k.value === v))
      .map((value) => ({ value, label: value }));
    return [...known, ...extra];
  }, [data, locale]);

  const save = async (values: ObjectionData) => {
    try {
      if (editing?.mode === "edit")
        await dp.update("objections", {
          id: editing.objection.id,
          data: values,
          previousData: editing.objection,
        });
      else await dp.create("objections", { data: values });
      notify("gavion.objections.settings.saved", { type: "success" });
      setEditing(null);
      await refetch();
    } catch (e) {
      notify("gavion.objections.settings.save_error", { type: "error" });
      throw e;
    }
  };

  const toggle = async (o: Objection) => {
    setBusy(o.id);
    try {
      await dp.update("objections", {
        id: o.id,
        data: { active: !o.active },
        previousData: o,
      });
      notify(
        o.active
          ? "gavion.objections.settings.deactivated"
          : "gavion.objections.settings.reactivated",
        { type: "success" },
      );
      await refetch();
    } catch {
      notify("gavion.objections.settings.save_error", { type: "error" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold">
          {t("gavion.objections.title")}
        </h1>
        {admin && (
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => setEditing({ mode: "create" })}
          >
            {t("gavion.objections.settings.new")}
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        {t("gavion.objections.settings.intro")}
      </p>
      {!admin && (
        <p className="text-sm text-muted-foreground">
          {t("gavion.objections.settings.read_only")}
        </p>
      )}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor={filterId}>
            {t("gavion.objections.settings.filter_trade")}
          </Label>
          <select
            id={filterId}
            className={SELECT_CLASS}
            value={trade}
            onChange={(e) => setTrade(e.target.value)}
          >
            <option value="all">{t("gavion.objections.settings.all")}</option>
            <option value="generic">
              {t("gavion.objections.settings.generic")}
            </option>
            {tradeChoices.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {admin && (
          <div className="flex items-center gap-2 pb-2">
            <Switch
              id={`${filterId}-inactive`}
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <Label htmlFor={`${filterId}-inactive`}>
              {t("gavion.objections.settings.show_inactive")}
            </Label>
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("gavion.objections.settings.none")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t("gavion.objections.settings.columns.objection")}
                </TableHead>
                <TableHead>
                  {t("gavion.objections.settings.columns.trade")}
                </TableHead>
                <TableHead>
                  {t("gavion.objections.settings.columns.tags")}
                </TableHead>
                <TableHead className="text-right">
                  {t("gavion.objections.settings.columns.helpful")}
                </TableHead>
                {admin && (
                  <>
                    <TableHead className="text-right">
                      {t("gavion.objections.settings.columns.order")}
                    </TableHead>
                    <TableHead>
                      {t("gavion.objections.settings.columns.status")}
                    </TableHead>
                    <TableHead>
                      {t("gavion.objections.settings.columns.actions")}
                    </TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((o) => (
                <TableRow key={o.id} className={o.active ? "" : "opacity-60"}>
                  <TableCell className="max-w-md whitespace-normal">
                    <p className="font-medium break-words">{o.objection}</p>
                    <p className="line-clamp-2 text-sm break-words text-muted-foreground">
                      {o.response}
                    </p>
                  </TableCell>
                  <TableCell>
                    {o.trade
                      ? tradeLabel(o.trade, locale)
                      : t("gavion.objections.panel.generic")}
                  </TableCell>
                  <TableCell className="whitespace-normal">
                    <div className="flex flex-wrap gap-1">
                      {(o.tags ?? []).map((tag) => (
                        <Badge key={tag} variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {o.helpful_count}
                  </TableCell>
                  {admin && (
                    <>
                      <TableCell className="text-right tabular-nums">
                        {o.sort_order}
                      </TableCell>
                      <TableCell>
                        {t(
                          `gavion.objections.settings.status.${o.active ? "active" : "inactive"}`,
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={t(
                              "gavion.objections.settings.edit_label",
                              { name: o.objection },
                            )}
                            onClick={() =>
                              setEditing({ mode: "edit", objection: o })
                            }
                          >
                            {t("gavion.objections.settings.edit")}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy === o.id}
                            aria-label={t(
                              o.active
                                ? "gavion.objections.settings.deactivate_label"
                                : "gavion.objections.settings.reactivate_label",
                              { name: o.objection },
                            )}
                            onClick={() => void toggle(o)}
                          >
                            {t(
                              o.active
                                ? "gavion.objections.settings.deactivate"
                                : "gavion.objections.settings.reactivate",
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {admin && editing && (
        <ObjectionEditor
          mode={editing.mode}
          initial={
            editing.mode === "edit"
              ? toObjectionForm(editing.objection)
              : emptyObjectionForm()
          }
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
};
